import {
  chooseDefaultAssignedTicket,
  chooseDefaultLiveChatTicket,
  chooseDefaultNominatedTicket,
} from '../util/chooseDefault';
import { selectTicket, setOutboundEscalationObject } from './index';
import * as creatorsSync from './creatorsSync';
import { createQueueObject, processNominatedOrReviewTicket } from '../util/dataObjectToStoreObject';
import {
  extractSelectedTicketFromState,
  extractSelectedTicketIdFromState,
  getSelectedInboxTab,
  getWhiteList,
  extractFirstMessage,
  getBrandOfSelectedTicket,
  extractCustomerNameForSelectedTicket,
  getPartnersAllowlist,
  getFlowDebugModeStatus,
} from '../extractFromState';
import { getEscalatedTabFilterParameters } from '../selectors/filters';
import { getSelectedTicketShopNamesFromBrand } from '../selectors/tools';
import { INBOUND } from 'constants/messageDirection';
import get from 'lodash/get';
import * as commerceTriggers from 'constants/commerceTriggers';
import * as parseUtil from 'util/parseSelectors';
import { commerceRecosFetched } from '../analytics/mlEventAnalytics';
import { isLiveChat } from 'util/chat';
import { getNominatedExpertQueues } from 'api/expert';
import { getOrdersIdsMentionedInTicket } from '../api/whatOrder';
import { getDefaultEscalationMessage } from '../api/partner';
import { getRecommendedProducts } from '../api/productRecommendations';
import { getAssignedTickets, getEscalatedTickets, getReviewTickets, getConversation } from '../api/tickets';
import { handleError } from 'services/ErrorService/ErrorService';

/*
functions that do some asynchronous operation like API calls and then resolve to an action or an array of actions
 */
const chooseDefaultTicket = (selectedTab, preferredSelectedTicketId, nominatedTickets, assignedTickets) => {
  switch (selectedTab) {
    case 'nominated':
      return nominatedTickets[preferredSelectedTicketId]
        ? preferredSelectedTicketId
        : chooseDefaultNominatedTicket({
            tickets: {
              nominatedTickets,
            },
          });
    case 'assigned':
      return assignedTickets[preferredSelectedTicketId]
        ? preferredSelectedTicketId
        : chooseDefaultAssignedTicket({
            tickets: {
              assignedTickets,
            },
          });
    case 'live_chat':
      return assignedTickets[preferredSelectedTicketId]
        ? preferredSelectedTicketId
        : chooseDefaultLiveChatTicket({
            tickets: {
              assignedTickets,
            },
          });

    default:
      throw new Error(`unknown selectedInboxTab ${selectedTab}`);
  }
};

/**
 * Makes API calls and returns actions which will select/load tab and ticket (if available)
 */
export const loadTicketsAdmin = async (state) => {
  let selectedTab = getSelectedInboxTab(state) || 'all';
  const whiteList = getWhiteList(state);
  const partnersAllowlist = getPartnersAllowlist(state);
  let preferredSelectedTicketId = extractSelectedTicketIdFromState(state);

  const [escalatedTickets, assignedTickets] = await Promise.all([
    getEscalatedTickets({ ...getEscalatedTabFilterParameters(state), whiteList, partnersAllowlist }),
    getAssignedTickets(true, partnersAllowlist),
  ]);

  const ticketHydrationActions = [
    creatorsSync.hydrateAssignedTickets(assignedTickets),
    creatorsSync.hydrateNominatedTickets(escalatedTickets),
  ];

  // NOTE(vlad|SPD-14347|2026-03-03): ALL tab uses search-button paradigm —
  // tickets load only when user clicks Search in AllTabFilters.
  // filterAllTickets (in allFilterActions.js) handles the query + hydration.
  if (selectedTab === 'all') {
    return [creatorsSync.changeTab(selectedTab), ...ticketHydrationActions];
  }

  let selectedTicketId = chooseDefaultTicket(selectedTab, preferredSelectedTicketId, escalatedTickets, assignedTickets);

  if (!selectedTicketId) {
    return [creatorsSync.changeTab(selectedTab), ...ticketHydrationActions];
  }

  const selectedTicket = escalatedTickets[selectedTicketId] || assignedTickets[selectedTicketId];
  const simplrAccountId = get(selectedTicket, 'simplrAccountPtrId');
  const conversation = await getConversation(selectedTicketId, getFlowDebugModeStatus(state), simplrAccountId);

  return [
    selectTicket(selectedTicketId, selectedTab),
    creatorsSync.changeTab(selectedTab),
    ...ticketHydrationActions,
    creatorsSync.showConversation(conversation),
  ];
};

/**
 * Makes API calls and returns actions which will select/load tab and ticket
 * @param state {object}
 * @return {Promise.<Array.<object>>}
 */
export const loadTicketsNonAdmin = async (state) => {
  const selectedTab = getSelectedInboxTab(state) || 'assigned';
  const preferredSelectedTicketId = extractSelectedTicketIdFromState(state);

  if (selectedTab === 'all') {
    throw new Error('non admin cannot select all tab');
  }

  const partnersAllowlist = getPartnersAllowlist(state);

  const [assignedTicketsObject, expertQueues, reviewTicketsList] = await Promise.all([
    getAssignedTickets(false, partnersAllowlist),
    getNominatedExpertQueues(),
    getReviewTickets({ partnersAllowlist }),
  ]);

  const processedNominatedTickets = expertQueues.reduce((result, expertQueue) => {
    try {
      const processed = processNominatedOrReviewTicket(expertQueue.conversationPtr, expertQueue.nominationExpiresAt);
      result.push(processed);
    } catch (error) {
      handleError({ error, customFields: { expertQueueObj: expertQueue } });
    }

    return result;
  }, []);
  const nominatedTicketsObject = createQueueObject(processedNominatedTickets);

  const processedReviewTickets = reviewTicketsList.map(processNominatedOrReviewTicket);
  const reviewTicketsObject = createQueueObject(processedReviewTickets);

  const ticketHydrationActions = [
    creatorsSync.hydrateAssignedTickets(assignedTicketsObject),
    creatorsSync.hydrateNominatedTickets(nominatedTicketsObject),
    creatorsSync.hydrateReviewTickets(reviewTicketsObject),
  ];

  let selectedTicketId = chooseDefaultTicket(
    selectedTab,
    preferredSelectedTicketId,
    nominatedTicketsObject,
    assignedTicketsObject,
    reviewTicketsObject
  );

  if (!selectedTicketId) {
    return [creatorsSync.changeTab(selectedTab), ...ticketHydrationActions];
  }
  const selectedTicket =
    assignedTicketsObject[selectedTicketId] ||
    nominatedTicketsObject[selectedTicketId] ||
    reviewTicketsObject[selectedTicketId];
  const simplrAccountId = get(selectedTicket, 'simplrAccountPtrId');
  const conversation = await getConversation(selectedTicketId, false, simplrAccountId);

  const actions = [
    selectTicket(selectedTicketId, selectedTab),

    creatorsSync.changeTab(selectedTab),
    ...ticketHydrationActions,

    // open conversation in right panel
    creatorsSync.showConversation(conversation),
  ];

  return actions;
};

// TODO: remove when escalation messages are consolidated
export const fetchDefaultEscalationMessage = () => async (dispatch, getState) => {
  const selectedTicket = extractSelectedTicketFromState(getState());
  dispatch(setOutboundEscalationObject(''));

  if (selectedTicket) {
    const { simplrAccountPtrId, ticketId } = selectedTicket;
    const message = await getDefaultEscalationMessage({ simplrAccountPtrId, conversationId: ticketId });

    // update escalation object only if the ticket is the same
    const { ticketId: ticketIdAfterFetch } = extractSelectedTicketFromState(getState());
    if (ticketId === ticketIdAfterFetch) {
      return dispatch(setOutboundEscalationObject(message));
    }
  }
};

export const getMlOrders = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const selectedTicket = extractSelectedTicketFromState(state);
    const selectedTicketId = selectedTicket && selectedTicket.ticketId;
    const firstMessage = extractFirstMessage(state);
    if (firstMessage) {
      const { subject, simplrAccountObjectId, customerPtrId } = firstMessage;
      const allMessages = state.conversation.allMessages.map((messageId) => state.conversation[messageId].messageBody);
      const result = await getOrdersIdsMentionedInTicket(
        allMessages,
        subject,
        simplrAccountObjectId,
        customerPtrId,
        selectedTicket
      );

      dispatch(creatorsSync.setMlOrdersIds(selectedTicketId, result));
    }
  } catch (error) {
    handleError({ error });
  }
};

export const getMLRecommendedProducts =
  (macroText, chatProductRecTrigger = false) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const selectedTicket = extractSelectedTicketFromState(state);
      const customerName = extractCustomerNameForSelectedTicket(state);
      const isChatTicket = isLiveChat(selectedTicket.communicationType);
      const ticketId = selectedTicket && selectedTicket.ticketId;
      const firstMessage = extractFirstMessage(state);
      const expertId = parseUtil.getExpertId();
      const incomingMessages = state.conversation.allMessages.filter(
        (messageId) => state.conversation[messageId].direction === INBOUND
      );
      const numCustomerMessagesOnLastRequest = get(
        state,
        `mlData.recommendedProducts[${ticketId}].numCustomerMessagesOnLastRequest`,
        0
      );
      const numberOfCustomerMessagesIncreased = incomingMessages.length > numCustomerMessagesOnLastRequest;

      const macroContainsTrigger = isChatTicket
        ? chatProductRecTrigger
        : macroText && macroText.includes(commerceTriggers.RECOMMEND_A_PRODUCT_TRIGGER);

      if (firstMessage && selectedTicket && numberOfCustomerMessagesIncreased && macroContainsTrigger) {
        const { subject, simplrAccountObjectId, customerPtrId } = firstMessage;
        const customerEmail = selectedTicket.customerEmail;
        const shopNames = getSelectedTicketShopNamesFromBrand(state);
        const brand = getBrandOfSelectedTicket(state);
        const message = incomingMessages.map((messageId) => state.conversation[messageId].messageBody);

        const result = await getRecommendedProducts({
          message,
          businessId: simplrAccountObjectId,
          customerId: customerPtrId,
          conversationId: ticketId,
          subject,
          shopNames,
          customerEmail,
          expertId,
          brand: brand ? [brand] : null,
          customerName,
        });

        const data = {
          allRecommendedProducts: get(result, 'data.allRecommendedProducts', []),
          globalProducts: get(result, 'data.globalProducts', []),
          crossSellProducts: get(result, 'data.crossSellProducts', []),
          detectionSource: get(result, 'data.requestDetails.detectionSource', ''),
          noShowReason: get(result, 'data.requestDetails.noShowReason'),
          productDetected: get(result, 'data.productDetected', {}),
          upSellProducts: get(result, 'data.upSellProducts', []),
          toDisplay: get(result, 'data.toDisplay', false),
          numCustomerMessagesOnLastRequest: incomingMessages.length,
          previouslyTriggeredRecommendations: false,
        };

        dispatch(creatorsSync.setRecommendedProductsForPartner(data, ticketId));

        commerceRecosFetched({
          toDisplay: get(result, 'data.toDisplay'),
          requestDetails: get(result, 'data.requestDetails'),
          detectedProductId: get(result, 'data.productDetected.id'),
        });
      }
    } catch (error) {
      handleError({ error });
    }
  };
